// janus-engine/ability/rolls/status-item-passive-entries.ts — ft_status_item_passive_entries() (creature_statuses.php:338), shared by the
// ability lane's caster extras (damage-type-roll.ts) and the weapon lane (actions/weapon-attack-extra-damage.ts).
//
// A passive ITEM applied to a creature directly as a status (the GM manual status adder; "Extra Damage - Cold (10d4)" on Sorcerer Guy,
// playtest 2026-10-16) never reaches the passive bucket, so PHP's damage loops append these entries AFTER the bucket and treat each one
// exactly like a bucket entry. The list, in PHP's order and with PHP's gates:
//   · payload statuses only — a hostile aura's self-anchor and a dormant owner-gated aura reach nobody (creature_payload_statuses);
//   · the definition's item_type is passive_ability (a status_effect is not a passive entry);
//   · deduped by passive id against the holder's ACTIVE bucket entries and every earlier admitted row (a gated-out row masks nothing);
//   · buff_does_not_layer_onto_opposing_team: a row a creature hostile to the holder applied is skipped (determine_if_target_is_hostile_
//     team_simplified on the applier's stored team; PHP's no-row answer is "not hostile", the page's unheld applier is a gap);
//   · check_ability_conditions(def, holder) — the browser twin (compile/ability-conditions.ts).
// The caller's `relevant(def)` scopes the gates to rows that could act in THIS use (an extra-damage row that would fire): a row that could not
// is never judged, so an unanswerable gate on it refuses nothing (Opus review 2026-10-16 — MAIN refused only a firing row).
// The shared scene evaluator answers contextual conditions; only missing inputs return a named pre-draw gap.
import { createAbilityConditionEvaluator } from '../../compile/ability-conditions.js';
import { answerConditionContext, ConditionContextUnknown, type ConditionScene } from '../../compile/condition-context.js';
import { C1_CONTRACT } from '../../compile/possessor-compile.js';
import { AuraOwnerGateUnavailable, isDormantOwnerGatedAura, isHostileAuraSelfAnchor } from '../../compile/creature-status-reader.js';
import { findInventoryRow } from '../../inventory/flag-adjust.js';
import { encounterCreatureField } from '../../../shared/array-types.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => { let x = v; while (Array.isArray(x)) x = x[0]; return x; };
const upper = (v: unknown): string => String(scalar(v) ?? '').toUpperCase();
const FRIEND = '7DBEB6D4-D147-703A-022E-05B290BB95C3', ENEMY = '9399856B-2132-7574-1684-91139A2831DD', NEUTRAL = 'BBF5D6A6-FB9B-E07D-0497-B60EC68D2EBD';

export interface StatusItemPassiveReaders {
  value: (row: Row, key: string) => unknown;
  entries: (row: Row, key: string) => unknown[];
  /** A full definition the page holds, or null. */
  definition: (id: string) => Row | null;
  /** The roster row of an encounter creature id; undefined when the page does not hold it. */
  member: (id: string) => Row | undefined;
  /** The host's held encounter and campaign, including current action-local roster changes. */
  conditionScene?: () => Pick<ConditionScene, 'encounter' | 'campaign'> | null;
  charges?: ConditionScene['charges'];
  /** Whether this (resolved) passive could act in this use — the gates run only for one that could. */
  relevant: (def: Row) => boolean;
  /** Action-local activation (e.g. the striking weapon), after status/aura admission. */
  activation?: (def: Row) => boolean | string;
}
export interface StatusItemPassive { status: Row; definition: Row; id: string }
export type StatusItemPassiveList = { entries: StatusItemPassive[] } | { gap: string };

class StatusPassiveGap extends Error { constructor(readonly gap: string) { super(gap); } }

/** encounter_row_team_uuid(): the stored team (encounter_creatures bucket, then the row, then the layer; a {items:{item_id}} shell unwrapped,
 *  upper-cased like combat/opposition.ts teamOf); null when unset. */
function teamOf(row: Row, read: StatusItemPassiveReaders): string | null {
  let team = scalar(encounterCreatureField(record(row.encounter_creatures), 'team') ?? row.team ?? read.value(row, 'team'));
  if (record(team)) team = scalar(record(record(team)?.items)?.item_id ?? record(team)?.item_id);
  return team === undefined || team === null || team === '' || typeof team === 'object' ? null : upper(team);
}
/** determine_if_target_is_hostile_team_simplified(). */
function hostileTeams(caster: string | null, target: string | null): boolean {
  if (!caster || !target) return false;
  if (caster === FRIEND || caster === NEUTRAL) return target === ENEMY;
  return caster === ENEMY && target === FRIEND;
}
const definitionId = (row: Row | null): string => upper(record(row?.items)?.item_id ?? row?.item_id);
const hasLayers = (row: Row | null): boolean => !!row && Object.keys(record(row.item_layer_types) ?? {}).length > 0;
/** passive_ability_entry_is_active(): a bare ref or an unstamped row is active; a stamped one by truthiness ('' / 0 / null = inactive). */
export function passiveEntryActive(entry: unknown): boolean {
  const row = record(entry);
  return !row || !('passive_conditions_active' in row) || present(row.passive_conditions_active);
}
/** ability_entry_id() (+ the bucket loop's item_arr fallback): legacy ability_id, a bare ref, item_id, then the nested item_arr's id. */
function entryId(entry: unknown): string {
  if (typeof entry === 'string') return upper(entry);
  const row = record(entry);
  if (!row) return '';
  if (typeof row.ability_id === 'string' && row.ability_id !== '') return upper(row.ability_id);
  if (typeof row.item_arr === 'string' && row.item_arr !== '') return upper(row.item_arr);
  if (!('item_arr' in row) && typeof row.item_id === 'string' && row.item_id !== '') return upper(row.item_id);
  return definitionId(record(row.item_arr) ?? row);
}

/**
 * The holder's status-applied passive entries, PHP's order. `statuses` are the holder's active target rows paired with their held
 * definitions (null = not held — the caller has already named that gap for a status it must read).
 */
export function statusItemPassiveEntries(holder: Row, statuses: ReadonlyArray<{ status: Row; definition: Row | null }>,
  read: StatusItemPassiveReaders): StatusItemPassiveList {
  const holderId = upper(encounterCreatureField(record(holder.encounter_creatures), 'encounter_creature_id'));
  let heldScene: Pick<ConditionScene, 'encounter' | 'campaign'> | undefined;
  const scene = (): Pick<ConditionScene, 'encounter' | 'campaign'> => heldScene ??= read.conditionScene?.()
    ?? { encounter: record(holder.encounter_arr), campaign: undefined };
  const member = (id: string): Row | null => read.member(id) ?? (id === holderId ? holder : null)
    ?? Object.values((scene().encounter?.encounter_creatures_arr ?? {}) as object).map(record)
      .find(row => upper(encounterCreatureField(record(row?.encounter_creatures), 'encounter_creature_id')) === id) ?? null;
  // An inactive bucket copy never rolls, so it must not mask an active status row.
  const seen = new Set(read.entries(holder, 'passive_ability').filter(passiveEntryActive).map(entryId).filter(Boolean));
  const out: StatusItemPassive[] = [];
  let conditions: ReturnType<typeof createAbilityConditionEvaluator> | null = null;
  try {
    for (const { status, definition } of statuses) {
      if (!definition || String(scalar(record(definition.items)?.item_type ?? definition.item_type) ?? '') !== 'passive_ability') continue;
      const id = definitionId(definition) || upper(status.status_item_id);
      if (!id || seen.has(id)) continue;
      let def: Row | null = definition;
      if (!hasLayers(def)) {
        // ft_resolve_layer_entry(): a layerless stub resolves to the full definition — one the page does not hold is fetched, never skipped
        def = read.definition(id);
        if (!def) return { gap: `status-passive-definition-not-held:${id}` };
        if (!hasLayers(def)) continue;
      }
      if (!read.relevant(def!)) continue;
      // A row THIS dispatch added carries `caster` / `target` (context.ts StatusRow), a stored one status_caster_id / status_target_id.
      // creature_payload_statuses(): an elided self-cast caster reads as the holder (readCreatureStatuses re-stamps it).
      const targetId = 'status_target_id' in status ? status.status_target_id : status.target;
      const storedCaster = 'status_caster_id' in status ? status.status_caster_id : status.caster; // an explicit null stays "not self"
      const casterId = storedCaster === undefined ? targetId : storedCaster;
      const layer = (row: unknown, key: string): unknown => { const r = record(row); return r ? read.value(r, key) : null; };
      if (isHostileAuraSelfAnchor(def, status.status_aoe_source, casterId, targetId, layer)) continue;
      try {
        if (isDormantOwnerGatedAura({ ...status, status_arr: def, status_caster_id: casterId }, layer,
          owner => {
            const held = member(upper(owner));
            if (!held) throw new AuraOwnerGateUnavailable();
            return held;
          }, holder)) continue;
      } catch (error) {
        if (error instanceof AuraOwnerGateUnavailable) return { gap: `status-passive-${error.reason}` };
        throw error;
      }
      const applier = upper(storedCaster);
      if (applier && applier !== holderId && present(read.value(def!, 'buff_does_not_layer_onto_opposing_team'))) {
        const holderTeam = teamOf(holder, read);
        if (holderTeam !== null) {
          const owner = member(applier);
          if (!owner) return { gap: `status-passive-applier-not-held:${applier}` };
          if (hostileTeams(teamOf(owner, read), holderTeam)) continue;
        }
      }
      const activation = read.activation?.(def!) ?? true;
      if (typeof activation === 'string') return { gap: activation };
      if (!activation) continue;
      conditions ??= createAbilityConditionEvaluator({ catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
        layerReaders: { layerValue: (row, key) => row ? read.value(row as Row, key) ?? null : null,
          layerEntries: (row, key) => row ? read.entries(row as Row, key) : [] },
        aggregationStrategy: name => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: ref => { const held = read.definition(upper(ref)); if (!held) throw new StatusPassiveGap(`status-passive-definition-not-held:${upper(ref)}`); return held; },
        resolveInventoryInstance: inventoryId => findInventoryRow(holder, inventoryId), queryTemplateDefinition: ref => read.definition(upper(ref)),
        equipmentSlots: C1_CONTRACT.equipmentSlots, vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries,
        conditionSource: (kind, args) => {
          try {
            return answerConditionContext(kind, args, { ...scene(), value: (row, key) => row ? read.value(row, key) : null,
              entries: (row, key) => row ? read.entries(row, key) : [], abilityTypes: C1_CONTRACT.abilityTypes,
              equipmentSlots: C1_CONTRACT.equipmentSlots, definition: read.definition, charges: read.charges });
          } catch (error) {
            if (error instanceof ConditionContextUnknown) throw new StatusPassiveGap(`status-passive-condition-context-not-held:${kind}`);
            throw error;
          }
        } });
      let met: boolean;
      // A row shape the evaluator cannot read is a named gap, never a fatal (Opus review P2).
      try { met = conditions.check(def!, holder); } catch (error) {
        if (error instanceof TypeError) return { gap: 'status-passive-condition-unreadable' };
        throw error;
      }
      if (!met) continue;
      seen.add(id);
      out.push({ status, definition: def!, id });
    }
  } catch (error) {
    if (error instanceof StatusPassiveGap) return { gap: error.gap };
    throw error;
  }
  return { entries: out };
}
function present(v: unknown): boolean {
  const x = scalar(v);
  return x !== undefined && x !== null && x !== '' && x !== 0 && x !== '0' && x !== false;
}
